import { PayloadAction, WithSlice, createSlice } from "@reduxjs/toolkit";
import { isEqual } from "lodash";
import { Pagination } from "@sscs/types";
import { rootReducer } from "Store/rootReducer";
import { FileStreamFilter, FileStreamSort } from "../types";

export type FilesSliceState = {
  createdFiles: string[];
  deletedFiles: string[];
  filter: FileStreamFilter;
  sort: FileStreamSort;
  pagination: Pagination;
};

export const initFilter: FileStreamFilter = {
  file_name: "",
  user: "ALL",
  approval_status: "ALL",
  deleted_in_days: null,
};

const initialState: FilesSliceState = {
  createdFiles: [],
  deletedFiles: [],
  filter: initFilter,
  sort: {
    property: "upload_date",
    reverse: true,
  },
  pagination: {
    currentPage: 1,
    itemsPerPage: 10,
  },
};

export const filesSlice = createSlice({
  name: "fileStream",
  initialState,
  reducers: {
    fileCreated: (state, action: PayloadAction<string[]>) => {
      state.createdFiles.push(...action.payload);
    },
    fileDeleted: (state, action: PayloadAction<string[]>) => {
      state.createdFiles = state.createdFiles.filter(
        (fileId) => !action.payload.includes(fileId),
      );
      state.deletedFiles.push(...action.payload);
    },
    filterUpdated: (state, action: PayloadAction<FileStreamFilter>) => {
      /* Debounce for the search bars runs on first render and on value change.
       *
       * Comparison makes sure that we do not reset pagination and created and
       * deleted files if filter did not actually change.
       *
       * It didn't matter before because filters would reset on first render anyway. */
      if (!isEqual(state.filter, action.payload)) {
        state.filter = action.payload;

        state.pagination.currentPage = 1;
        state.createdFiles = [];
        state.deletedFiles = [];
      }
    },
    sortUpdated: (state, action: PayloadAction<FileStreamSort>) => {
      state.sort = action.payload;

      state.pagination.currentPage = 1;
      state.createdFiles = [];
      state.deletedFiles = [];
    },
    paginationUpdated: (state, action: PayloadAction<Pagination>) => {
      state.pagination = action.payload;

      state.createdFiles = [];
      state.deletedFiles = [];
    },
    clearFiles: (state) => {
      state.createdFiles = [];
      state.deletedFiles = [];
    },
  },
  selectors: {
    selectCreatedFiles: (state): string[] => state.createdFiles,
    selectDeletedFiles: (state): string[] => state.deletedFiles,
    selectFilesFilter: (state) => state.filter,
    selectFilesSort: (state) => state.sort,
    selectFilesPagination: (state) => state.pagination,
  },
});

const injectedSlice = filesSlice.injectInto(rootReducer);

export const {
  fileCreated,
  fileDeleted,
  filterUpdated,
  paginationUpdated,
  sortUpdated,
  clearFiles,
} = filesSlice.actions;

export const {
  selectCreatedFiles,
  selectDeletedFiles,
  selectFilesFilter,
  selectFilesSort,
  selectFilesPagination,
} = injectedSlice.selectors;

declare module "Store/rootReducer" {
  export interface LazyLoadedSlices extends WithSlice<typeof filesSlice> {}
}
